import React, { useState, useEffect, Component } from 'react';
import {
  AppBar,
  Toolbar,
  Container,
  Box,
  Grid,
  Typography,
  Paper,
  CircularProgress,
  TextField,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import axios from 'axios';
import moment from 'moment';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

// Error boundary that ignores errors (renders nothing if an error occurs)
class IgnoreErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true };
  }
  componentDidCatch(error, info) {
    console.error("Ignored error in ToastContainer:", error, info);
  }
  render() {
    if (this.state.hasError) {
      return null;
    }
    return this.props.children;
  }
}

const Dashboard = () => {
  // -----------------------
  // Original single-day state
  // -----------------------
  const [allOrdersData, setAllOrdersData] = useState([]);
  const [filteredOrdersData, setFilteredOrdersData] = useState([]);
  const [allTasksData, setAllTasksData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [date, setDate] = useState('');

  const [stats, setStats] = useState({
    totalOrders: 0,
    pendingOrders: 0,
    pendingRevenue: 0,
    completedOrders: 0,
    completedRevenue: 0,
    cancelledOrders: 0,
    totalRevenue: 0,
    totalSales: 0,
    totalDeliveryCharges: 0,
    totalOfTotalCharges: 0,
    completedTasks: 0,
    pendingTasks: 0,
  });

  // -----------------------
  // Notification (existing)
  // -----------------------
  const [open, setOpen] = useState(false);
  const [notification, setNotification] = useState({
    title: '',
    description: '',
    details: ''
  });

  // -----------------------
  // New date-range states
  // -----------------------
  const [rangeStartDate, setRangeStartDate] = useState('');
  const [rangeEndDate, setRangeEndDate] = useState('');
  const [rangeStats, setRangeStats] = useState({
    totalOrders: 0,
    pendingOrders: 0,
    pendingRevenue: 0,
    completedOrders: 0,
    completedRevenue: 0,
    cancelledOrders: 0,
    totalSales: 0,
    totalDeliveryCharges: 0,
    totalRevenue: 0, // net = totalSales - totalDeliveryCharges
    kirupamShare: 0,
    storeShare: 0,
  });

  // ---------------------------------
  // Fetch Data (existing single-day code)
  // ---------------------------------
  const fetchAllData = async () => {
    setLoading(true);
    try {
      const storedOrders = localStorage.getItem('dashboardOrdersData');
      const storedTasks = localStorage.getItem('dashboardTasksData');

      if (storedOrders && storedTasks) {
        setAllOrdersData(JSON.parse(storedOrders));
        setAllTasksData(JSON.parse(storedTasks));
      } else {
        const [ordersResponse, tasksResponse] = await Promise.all([
          axios.get('https://kirupam.shop/admin/admin_api/orders_sat.php'),
          axios.get('https://kirupam.shop/admin/admin_api/tasks.php'),
        ]);
        const ordersData = Array.isArray(ordersResponse.data) ? ordersResponse.data : [];
        const tasksData = Array.isArray(tasksResponse.data) ? tasksResponse.data : [];

        setAllOrdersData(ordersData);
        setAllTasksData(tasksData);

        localStorage.setItem('dashboardOrdersData', JSON.stringify(ordersData));
        localStorage.setItem('dashboardTasksData', JSON.stringify(tasksData));
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error fetching data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  // ---------------------------------
  // Single-Day Filter
  // ---------------------------------
  useEffect(() => {
    if (date) {
      filterData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, allOrdersData, allTasksData]);

  const filterData = () => {
    const selectedDate = moment(date).format('YYYY-MM-DD');
    const filteredOrders = allOrdersData.filter(
      order => moment(order.selected_date).format('YYYY-MM-DD') === selectedDate
    );
    const filteredTasks = allTasksData.filter(
      task => moment(task.created_at).format('YYYY-MM-DD') === selectedDate
    );

    const totalOrders = filteredOrders.length;
    const pendingOrders = filteredOrders.filter(order => order.order_status === 'Pending').length;
    const completedOrders = filteredOrders.filter(order => order.order_status === 'Completed').length;
    const cancelledOrders = filteredOrders.filter(order => order.order_status === 'Cancelled').length;

    const pendingRevenue = filteredOrders.reduce((acc, order) => {
      const totalCharge = parseFloat(order.totalCharge) || 0;
      const deliveryCharge = parseFloat(order.delivery_charge) || 0;
      return acc + (order.order_status === 'Pending' ? totalCharge + deliveryCharge : 0);
    }, 0);

    const completedRevenue = filteredOrders.reduce((acc, order) => {
      const totalCharge = parseFloat(order.totalCharge) || 0;
      const deliveryCharge = parseFloat(order.delivery_charge) || 0;
      return acc + (order.order_status === 'Completed' ? totalCharge + deliveryCharge : 0);
    }, 0);

    const totalRevenue = pendingRevenue + completedRevenue;
    const totalSales = filteredOrders.reduce((acc, order) => acc + (parseFloat(order.totalCharge) || 0), 0);
    const totalDeliveryCharges = filteredOrders.reduce(
      (acc, order) => acc + (parseFloat(order.delivery_charge) || 0),
      0
    );
    const totalOfTotalCharges = totalSales + totalDeliveryCharges;

    const completedTasks = filteredTasks.filter(task => task.task_status === 'completed').length;
    const pendingTasks = filteredTasks.filter(task => task.task_status === 'pending').length;

    setStats({
      totalOrders,
      pendingOrders,
      pendingRevenue,
      completedOrders,
      completedRevenue,
      cancelledOrders,
      totalRevenue,
      totalSales,
      totalDeliveryCharges,
      totalOfTotalCharges,
      completedTasks,
      pendingTasks,
    });
    setFilteredOrdersData(processOrdersData(filteredOrders, selectedDate));
  };

  const processOrdersData = (orders, selectedDate) => {
    const data = { date: selectedDate, Pending: 0, Completed: 0, Cancelled: 0 };
    orders.forEach(order => {
      if (order.order_status === 'Pending') data.Pending++;
      if (order.order_status === 'Completed') data.Completed++;
      if (order.order_status === 'Cancelled') data.Cancelled++;
    });
    return [data];
  };

  // ---------------------------------
  // New: Date Range Filter
  // ---------------------------------
  const filterRangeData = () => {
    if (!rangeStartDate || !rangeEndDate) return;

    const filteredOrders = allOrdersData.filter(order => {
      const orderDate = moment(order.selected_date).format('YYYY-MM-DD');
      return moment(orderDate).isBetween(rangeStartDate, rangeEndDate, 'day', '[]');
    });

    const totalOrders = filteredOrders.length;
    const pendingOrders = filteredOrders.filter(order => order.order_status === 'Pending').length;
    const completedOrders = filteredOrders.filter(order => order.order_status === 'Completed').length;
    const cancelledOrders = filteredOrders.filter(order => order.order_status === 'Cancelled').length;

    const pendingRevenue = filteredOrders.reduce((acc, order) => {
      const totalCharge = parseFloat(order.totalCharge) || 0;
      const deliveryCharge = parseFloat(order.delivery_charge) || 0;
      return acc + (order.order_status === 'Pending' ? totalCharge + deliveryCharge : 0);
    }, 0);

    const completedRevenue = filteredOrders.reduce((acc, order) => {
      const totalCharge = parseFloat(order.totalCharge) || 0;
      const deliveryCharge = parseFloat(order.delivery_charge) || 0;
      return acc + (order.order_status === 'Completed' ? totalCharge + deliveryCharge : 0);
    }, 0);

    // For the range, calculate net revenue as totalSales - totalDeliveryCharges
    const totalSales = filteredOrders.reduce((acc, order) => acc + (parseFloat(order.totalCharge) || 0), 0);
    const totalDeliveryCharges = filteredOrders.reduce(
      (acc, order) => acc + (parseFloat(order.delivery_charge) || 0),
      0
    );
    const netRevenue = totalSales - totalDeliveryCharges;
    const kirupamShare = netRevenue * 0.1;
    const storeShare = netRevenue * 0.9;

    setRangeStats({
      totalOrders,
      pendingOrders,
      pendingRevenue,
      completedOrders,
      completedRevenue,
      cancelledOrders,
      totalSales,
      totalDeliveryCharges,
      totalRevenue: netRevenue, // net revenue after subtracting delivery charges
      kirupamShare,
      storeShare
    });
  };

  // ---------------------------------
  // Notification logic (existing)
  // ---------------------------------
  const handleClickOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNotification({ ...notification, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      formData.append('title', notification.title);
      formData.append('description', notification.description);
      formData.append('details', notification.details);

      const response = await axios.post('https://kirupam.shop/admin/admin_api/add_notification.php', formData);
      if (response.data.success) {
        toast.success('Notification added successfully');
        handleClose();
      } else {
        toast.error('Failed to add notification');
      }
    } catch (error) {
      toast.error('Error adding notification');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'LKR' }).format(amount);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
    

      {/* Header */}
      <AppBar position="static" sx={{ mb: 4 }}>
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Admin Dashboard
          </Typography>
        </Toolbar>
      </AppBar>

      <Container>
        {/* 1) Single-Day Filter Section */}
        <Typography variant="h5" sx={{ mb: 2 }}>
          Single Day Selection
        </Typography>
        <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={2} alignItems="center" mb={4}>
          <TextField
            label="Select Date"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
          <Button variant="contained" color="primary" onClick={filterData}>
            Apply Filter
          </Button>
          <Button variant="outlined" color="secondary" onClick={handleClickOpen}>
            Add Notification
          </Button>
        </Box>

        {/* Single-Day Stats */}
        <Grid container spacing={3} mb={4}>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="subtitle2">Total Orders</Typography>
              <Typography variant="h4">{stats.totalOrders}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="subtitle2">Pending Orders</Typography>
              <Typography variant="h4">{stats.pendingOrders}</Typography>
              <Typography variant="caption">
                {formatCurrency(stats.pendingRevenue - stats.cancelledOrders)}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="subtitle2">Completed Orders</Typography>
              <Typography variant="h4">{stats.completedOrders}</Typography>
              <Typography variant="caption">
                {formatCurrency(stats.completedRevenue)}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="subtitle2">Cancelled Orders</Typography>
              <Typography variant="h4">{stats.cancelledOrders}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="subtitle2">Total Revenue</Typography>
              <Typography variant="h4">{formatCurrency(stats.totalRevenue)}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="subtitle2">Total Delivery Charges</Typography>
              <Typography variant="h4">{formatCurrency(stats.totalDeliveryCharges)}</Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* 2) Date Range Filter Section */}
        <Typography variant="h5" sx={{ mb: 2 }}>
          Date Range Selection
        </Typography>
        <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={2} alignItems="center" mb={4}>
          <TextField
            label="Range Start Date"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={rangeStartDate}
            onChange={(e) => setRangeStartDate(e.target.value)}
          />
          <TextField
            label="Range End Date"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={rangeEndDate}
            onChange={(e) => setRangeEndDate(e.target.value)}
          />
          <Button variant="contained" color="primary" onClick={filterRangeData}>
            Apply Range Filter
          </Button>
        </Box>

        {/* Range Summary (only if both dates selected) */}
        {rangeStartDate && rangeEndDate && (
          <Paper sx={{ p: 2, mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              Date Range Calculation Summary ({rangeStartDate} - {rangeEndDate})
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Typography>Total Orders: {rangeStats.totalOrders}</Typography>
                <Typography>Pending Orders: {rangeStats.pendingOrders}</Typography>
                <Typography>Completed Orders: {rangeStats.completedOrders}</Typography>
                <Typography>Cancelled Orders: {rangeStats.cancelledOrders}</Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography>Total Sales: {formatCurrency(rangeStats.totalSales)}</Typography>
                <Typography>Delivery Charges: {formatCurrency(rangeStats.totalDeliveryCharges)}</Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                {/* netRevenue = totalSales - totalDeliveryCharges */}
                <Typography>
                  Net Revenue (Sales - Delivery): {formatCurrency(rangeStats.totalRevenue)}
                </Typography>
                <Typography>Kirupam (10%): {formatCurrency(rangeStats.kirupamShare)}</Typography>
                <Typography>Store (90%): {formatCurrency(rangeStats.storeShare)}</Typography>
              </Grid>
            </Grid>
          </Paper>
        )}

        {/* Charts Section (single-day charts) */}
        <Grid container spacing={4} mt={2}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Order Status Breakdown
              </Typography>
              <PieChart width={350} height={300}>
                <Pie data={filteredOrdersData} dataKey="Pending" cx="50%" cy="50%" outerRadius={80} label>
                  {filteredOrdersData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Revenue Breakdown
              </Typography>
              <PieChart width={350} height={300}>
                <Pie
                  data={[
                    { name: 'Pending Revenue', value: stats.pendingRevenue },
                    { name: 'Completed Revenue', value: stats.completedRevenue },
                    { name: 'Cancelled Revenue', value: 0 }
                  ]}
                  dataKey="value"
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  label
                >
                  {[
                    { name: 'Pending Revenue', value: stats.pendingRevenue },
                    { name: 'Completed Revenue', value: stats.completedRevenue },
                    { name: 'Cancelled Revenue', value: 0 }
                  ].map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Task Analysis
              </Typography>
              <PieChart width={350} height={300}>
                <Pie
                  data={[
                    { name: 'Pending Tasks', value: stats.pendingTasks },
                    { name: 'Completed Tasks', value: stats.completedTasks }
                  ]}
                  dataKey="value"
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  label
                >
                  {[
                    { name: 'Pending Tasks', value: stats.pendingTasks },
                    { name: 'Completed Tasks', value: stats.completedTasks }
                  ].map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Orders Over Time
              </Typography>
              <BarChart
                width={350}
                height={300}
                data={filteredOrdersData}
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="Pending" fill="#ffbb33" />
                <Bar dataKey="Completed" fill="#00C49F" />
                <Bar dataKey="Cancelled" fill="#FF4444" />
              </BarChart>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Notification Dialog (existing) */}
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>Add Notification</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            name="title"
            label="Title"
            type="text"
            fullWidth
            value={notification.title}
            onChange={handleInputChange}
          />
          <TextField
            margin="dense"
            name="description"
            label="Description"
            type="text"
            fullWidth
            value={notification.description}
            onChange={handleInputChange}
          />
          <TextField
            margin="dense"
            name="details"
            label="Details"
            type="text"
            fullWidth
            value={notification.details}
            onChange={handleInputChange}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Cancel
          </Button>
          <Button onClick={handleSubmit} color="primary">
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Dashboard;
